/**
 * What the documents that read `contentsByAssignmentStage` — the stage counts and the four stage pages —
 * share. A leaf module on purpose: the documents use it, and the mutations (`mutationEffects.ts` imports
 * the documents to recognise them) build on it, so neither has to import the other.
 */

/**
 * The `@connection` key every stage read stores its `listContentLabelCategories(teamId)` answer under,
 * instead of the plain field the category LIST writes.
 *
 * There is no other root field to read a category's stages through, so the list and the stage reads
 * select the same one — and without a key of their own each answer would REPLACE the root array the
 * others read (the field has no merge, and a union merge would bring back categories deleted
 * elsewhere). A stage read answered before a category was created would then take the new card out
 * of the list for good: every read would be complete again, so nothing would ask the server. With the
 * key, only the list and `createContentLabelCategory` write the array the page renders; the category
 * objects themselves are still shared, keyed by id, so renames and new stage data reach every read.
 *
 * `filter: ["teamId"]` keeps one array per team — a bare key would drop the argument, and a team
 * switch would read the previous team's categories as complete.
 */
export const STAGE_READS_CONNECTION_KEY = 'contentLabelStageReads';

/**
 * Whether a `Query.listContentLabelCategories` store field name (as a `cache.modify` modifier sees it)
 * is a stage read's array rather than a category list. Apollo names the keyed field
 * `listContentLabelCategories:contentLabelStageReads({"teamId":…})`.
 */
export const isStageReadsStoreField = (storeFieldName: string): boolean =>
  storeFieldName.includes(`:${STAGE_READS_CONNECTION_KEY}(`);

/**
 * `useQuery` context for every stage read: request deduplication OFF.
 *
 * After a mutation that moves contents between stages, `mutationEffects.activeStageReads` has every
 * stage read on screen asked again, so the answer the cache keeps is one computed AFTER the mutation.
 * With deduplication on, that refetch would not be a request of its own whenever an identical one is
 * still in flight (the previous mutation's refetch, a slow first load): it would join it, and hand back
 * an answer computed BEFORE the mutation as the newest one. Apollo keeps only the latest request's
 * answer per query (`requestId >= lastRequestId` in `QueryManager.getResultsFromLink`), so with a
 * request of its own the stale answer is dropped when it lands.
 *
 * The cost: two stage reads with the same document and variables mounted in the same tick each send
 * their own request, and each is asked again on its own after a mutation. Mount each once and hand the
 * result down.
 */
export const STAGE_READ_CONTEXT = { queryDeduplication: false } as const;
